import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { safeLocalStorage } from '../lib/storage';

export interface VoiceChunk {
  text: string;
  elementId?: string;
}

export interface VoiceReaderContextType {
  isAvailable: boolean;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  toggleActive: () => void;
  isPlaying: boolean;
  isPaused: boolean;
  currentText: string;
  currentTitle: string;
  currentChunkIndex: number;
  totalChunks: number;
  rate: number;
  setRate: (rate: number) => void;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  setSelectedVoice: (voice: SpeechSynthesisVoice | null) => void;
  speakText: (text: string, title?: string, elementId?: string) => void;
  speakCurrentPage: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  nextChunk: () => void;
  prevChunk: () => void;
  activeElementId: string | null;
}

const VoiceReaderContext = createContext<VoiceReaderContextType | null>(null);

export const useVoiceReader = (): VoiceReaderContextType => {
  const context = useContext(VoiceReaderContext);
  if (!context) {
    throw new Error('useVoiceReader must be used within a VoiceReaderProvider');
  }
  return context;
};

// Helper to chunk long text into natural sentences/clauses
function splitIntoChunks(text: string): string[] {
  const clean = text
    .replace(/\r\n/g, '\n')
    .replace(/\n+/g, ' ')
    .trim();

  if (!clean) return [];

  // Match sentences ending with punctuation, quotes, or colons
  const regex = /[^.!?:]+[.!?:]+(["'”’]?)|[^.!?:]+$/g;
  const rawMatches = clean.match(regex) || [clean];

  const result: string[] = [];
  for (const match of rawMatches) {
    const trimmed = match.trim();
    if (!trimmed) continue;

    // If chunk is too long (> 200 chars), split by commas/semicolons
    if (trimmed.length > 220) {
      const subParts = trimmed.split(/([,;]|\s+—\s+)/);
      let buffer = '';
      for (const part of subParts) {
        if ((buffer + part).length > 200 && buffer.trim()) {
          result.push(buffer.trim());
          buffer = part;
        } else {
          buffer += part;
        }
      }
      if (buffer.trim()) {
        result.push(buffer.trim());
      }
    } else {
      result.push(trimmed);
    }
  }

  return result.filter(r => r.length > 0);
}

export const VoiceReaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(() => {
    return safeLocalStorage.getItem('family_tts_active') === 'true';
  });

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [currentText, setCurrentText] = useState<string>('');
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const [currentChunkIndex, setCurrentChunkIndex] = useState<number>(0);
  const [chunks, setChunks] = useState<VoiceChunk[]>([]);
  const [rate, setRate] = useState<number>(1.0);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [activeElementId, setActiveElementId] = useState<string | null>(null);

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const chunksRef = useRef<VoiceChunk[]>([]);
  chunksRef.current = chunks;
  const chunkIndexRef = useRef<number>(0);
  chunkIndexRef.current = currentChunkIndex;
  const rateRef = useRef<number>(rate);
  rateRef.current = rate;
  const selectedVoiceRef = useRef<SpeechSynthesisVoice | null>(selectedVoice);
  selectedVoiceRef.current = selectedVoice;

  // Check speech synthesis support and load voices safely
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis) {
        setIsAvailable(true);

        const updateVoices = () => {
          try {
            const availableVoices = window.speechSynthesis.getVoices() || [];
            if (availableVoices.length > 0) {
              setVoices(availableVoices);
              
              // Select best default voice: English (UK, NZ, AU, US) or Google/Natural voice
              if (!selectedVoiceRef.current) {
                const preferred = availableVoices.find(v => 
                  (v.lang.startsWith('en-GB') || v.lang.startsWith('en-NZ') || v.lang.startsWith('en-AU') || v.lang.startsWith('en-US')) &&
                  (v.name.includes('Natural') || v.name.includes('Google') || v.name.includes('Neural') || v.name.includes('Samantha') || v.name.includes('Daniel') || v.name.includes('Microsoft'))
                ) || availableVoices.find(v => v.lang.startsWith('en')) || availableVoices[0];
                
                setSelectedVoice(preferred || null);
              }
            }
          } catch (err) {
            console.warn('Voice update error in browser:', err);
          }
        };

        updateVoices();
        window.speechSynthesis.onvoiceschanged = updateVoices;
      }
    } catch (e) {
      console.warn('Speech synthesis initialization failed:', e);
    }
  }, []);

  // Save active state to storage
  useEffect(() => {
    safeLocalStorage.setItem('family_tts_active', isActive ? 'true' : 'false');
  }, [isActive]);

  const stop = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    } catch (e) {
      console.warn('Speech cancel error:', e);
    }
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentText('');
    setActiveElementId(null);
  }, []);

  const playChunkAtIndex = useCallback((index: number) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const currentChunks = chunksRef.current;
    if (index < 0 || index >= currentChunks.length) {
      stop();
      return;
    }

    window.speechSynthesis.cancel();

    const chunk = currentChunks[index];
    setCurrentChunkIndex(index);
    setCurrentText(chunk.text);
    if (chunk.elementId) {
      setActiveElementId(chunk.elementId);
      // Attempt scroll to element gently
      const el = document.getElementById(chunk.elementId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    const utterance = new SpeechSynthesisUtterance(chunk.text);
    utteranceRef.current = utterance;
    utterance.rate = rateRef.current;
    if (selectedVoiceRef.current) {
      utterance.voice = selectedVoiceRef.current;
    }

    utterance.onend = () => {
      if (chunkIndexRef.current + 1 < chunksRef.current.length) {
        playChunkAtIndex(chunkIndexRef.current + 1);
      } else {
        stop();
      }
    };

    utterance.onerror = (e) => {
      console.warn('Speech synthesis error:', e);
      if (e.error !== 'canceled') {
        stop();
      }
    };

    setIsPlaying(true);
    setIsPaused(false);
    window.speechSynthesis.speak(utterance);
  }, [stop]);

  const speakText = useCallback((text: string, title?: string, elementId?: string) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    
    // Automatically turn on Voice Reader mode if not active
    setIsActive(true);

    const stringChunks = splitIntoChunks(text);
    if (stringChunks.length === 0) return;

    const voiceChunks: VoiceChunk[] = stringChunks.map(str => ({
      text: str,
      elementId
    }));

    setChunks(voiceChunks);
    chunksRef.current = voiceChunks;
    setCurrentTitle(title || 'Spoken Passage');
    playChunkAtIndex(0);
  }, [playChunkAtIndex]);

  const speakCurrentPage = useCallback(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    // Scan main container for readable text blocks (headings, paragraphs, blockquotes)
    const mainEl = document.querySelector('main');
    if (!mainEl) return;

    const readableElements = mainEl.querySelectorAll('h1, h2, h3, p, blockquote, .archival-narrative');
    const gatheredChunks: VoiceChunk[] = [];

    readableElements.forEach((el, elIdx) => {
      const text = (el.textContent || '').trim();
      // Skip very short UI elements or breadcrumbs
      if (text.length < 15) return;
      if (el.closest('.no-voice-read') || el.closest('button') || el.closest('nav')) return;

      // Assign a temporary or existing ID for visual highlighting
      let id = el.id;
      if (!id) {
        id = `readable-chunk-${elIdx}`;
        el.id = id;
      }

      const sentences = splitIntoChunks(text);
      for (const sent of sentences) {
        gatheredChunks.push({
          text: sent,
          elementId: id
        });
      }
    });

    if (gatheredChunks.length === 0) {
      speakText('No readable content was found on this page.', 'Notice');
      return;
    }

    // Get current page heading for title
    const pageHeading = mainEl.querySelector('h1')?.textContent || 'Current Page';
    setChunks(gatheredChunks);
    chunksRef.current = gatheredChunks;
    setCurrentTitle(pageHeading);
    setIsActive(true);
    playChunkAtIndex(0);
  }, [playChunkAtIndex, speakText]);

  const pause = useCallback(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window && isPlaying) {
      window.speechSynthesis.pause();
      setIsPaused(true);
      setIsPlaying(false);
    }
  }, [isPlaying]);

  const resume = useCallback(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window && isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      setIsPlaying(true);
    } else if (chunks.length > 0) {
      playChunkAtIndex(currentChunkIndex);
    } else {
      speakCurrentPage();
    }
  }, [isPaused, chunks.length, currentChunkIndex, playChunkAtIndex, speakCurrentPage]);

  const nextChunk = useCallback(() => {
    if (currentChunkIndex + 1 < chunks.length) {
      playChunkAtIndex(currentChunkIndex + 1);
    } else {
      stop();
    }
  }, [currentChunkIndex, chunks.length, playChunkAtIndex, stop]);

  const prevChunk = useCallback(() => {
    if (currentChunkIndex > 0) {
      playChunkAtIndex(currentChunkIndex - 1);
    } else {
      playChunkAtIndex(0);
    }
  }, [currentChunkIndex, playChunkAtIndex]);

  const toggleActive = useCallback(() => {
    setIsActive(prev => {
      const next = !prev;
      if (!next) {
        stop();
      }
      return next;
    });
  }, [stop]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  return (
    <VoiceReaderContext.Provider
      value={{
        isAvailable,
        isActive,
        setIsActive,
        toggleActive,
        isPlaying,
        isPaused,
        currentText,
        currentTitle,
        currentChunkIndex,
        totalChunks: chunks.length,
        rate,
        setRate: (newRate) => {
          setRate(newRate);
          if (isPlaying) {
            playChunkAtIndex(currentChunkIndex);
          }
        },
        voices,
        selectedVoice,
        setSelectedVoice: (voice) => {
          setSelectedVoice(voice);
          if (isPlaying) {
            playChunkAtIndex(currentChunkIndex);
          }
        },
        speakText,
        speakCurrentPage,
        pause,
        resume,
        stop,
        nextChunk,
        prevChunk,
        activeElementId,
      }}
    >
      {children}
    </VoiceReaderContext.Provider>
  );
};
